import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Injectable,
  DestroyRef,
  inject,
  signal,
  afterEveryRender,
  LOCALE_ID,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { MatCalendarCellClassFunction, MatDatepickerIntl } from "@angular/material/datepicker";
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from "@angular/material-moment-adapter";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import moment from "moment";
import { MatSnackBar } from "@angular/material/snack-bar";
import * as echarts from "echarts";

import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';

import {
  DatabaseCacheService,
  Breadcrumb,
} from "../../../data/cache/database-cache.service";
import { CreateUserDataService } from "../../../data/user/create-user-data.service";
import { ConfirmDialogComponent } from "../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component";

import { UserSummaryFormService } from "./user-summary-form.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { delay } from "rxjs";
import {
  UserSummaryViewModel,
  UserSummaryState,
} from "./user-summary.viewmodel";
import {
  KT_CHART_DISPLAY_ITEMS,
  buildKtChartMergeOption,
  createKtChartBaseOption,
  calculateKtChartSizing,
} from "./user-summary-chart.config";
import { PdfOption, UserSummaryPdfService } from "./user-summary-pdf.service";
import { USER_SUMMARY_I18N, getWeekdaysArray } from "./user-summary.i18n";
import { JapaneseDatepickerIntl } from "@app/i18n/datepicker-ja.intl";

export const MY_FORMATS = {
  parse: {
    dateInput: "YYYY/MM/DD",
  },
  display: {
    dateInput: "YYYY/MM/DD",
    monthLabel: "MMMM",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@Injectable()
class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: "long" | "short" | "narrow") {
    return getWeekdaysArray();
  }
  getFirstDayOfWeek(): number {
    return 0;
  }
}

@Component({
  imports: [...SHARED_IMPORTS],
  selector: "app-user-summary",
  templateUrl: "./user-summary.component.html",
  styleUrls: ["./user-summary.component.scss"],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeId: string) =>
        (localeId || "").toLowerCase().startsWith("en") ? "en-US" : "ja-JP",
      deps: [LOCALE_ID],
    },
    { provide: MatDatepickerIntl, useClass: JapaneseDatepickerIntl },
    UserSummaryViewModel,
  ],
  host: {
    "(window:resize)": "onResize()",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSummaryComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly i18n = USER_SUMMARY_I18N;
  readonly isLoading = signal(true);
  readonly isSaving = signal(false);
  /** 長文テキストフィールドの展開状態 (field name → expanded) */
  readonly expandedFields = signal<Record<string, boolean>>({});
  /** 3行超のオーバーフローが検出されたフィールド */
  readonly overflowingFields = signal<Set<string>>(new Set());
  filteredStaffOptions: { [key: string]: string[] } = {};

  @ViewChild("chartElement", { static: false }) chartElementRef: ElementRef;
  private isChartInitialized = false;
  private pdfChartImageDataUrl: string | null = null;
  private pdfChartGenerationPromise: Promise<string | null> | null = null;

  error: any;
  isAnonymous = false;

  KTChartValues = new Array(KT_CHART_DISPLAY_ITEMS.length).fill(0);
  readonly KTChartDisplay = KT_CHART_DISPLAY_ITEMS.map((item) => item.label);
  readonly KTChartDiscliptions = KT_CHART_DISPLAY_ITEMS.map(
    (item) => item.description,
  );
  KTchartOption = createKtChartBaseOption();
  dynamicData = buildKtChartMergeOption(this.KTChartValues);

  userId = "";

  selectedUserWithUserData = null;
  selectedSectionGroupUserDataAttributes = [];
  userDataList = [];

  /** グループ名 → ラベルサイズクラスのキャッシュ（テンプレートから参照） */
  labelSizeClassMap: Record<string, string> = {};

  userDataId0 = null;
  userDataId1 = null;

  userDataDate0 = null;
  userDataDate1 = null;

  uploadAsOfDate = null;

  modeEnum = Mode;
  mode = Mode.display;
  inputValidationErrors = [];

  breadcrumbs: Breadcrumb[];

  pdfOptions: PdfOption[];
  filteredPdfOptions: PdfOption[] = [];
  SelectedSectionGroupFunctions = [];
  isPdfOptionsExpanded = true;
  private isDesktopSummaryLayout = true;

  get isDownloadPdfButtonDisabled(): boolean {
    return this.userDataDate0 == null;
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly hostRef = inject(ElementRef);
  private readonly viewModel = inject(UserSummaryViewModel);
  readonly vm$ = this.viewModel.vm$;

  constructor(
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private common: DatabaseCacheService,
    private createUserDataService: CreateUserDataService,
    private snackBar: MatSnackBar,
    private readonly userSummaryPdfService: UserSummaryPdfService,
    private readonly formService: UserSummaryFormService,
  ) {
    this.pdfOptions = this.userSummaryPdfService.createPdfOptions();
    this.filteredPdfOptions = [];

    if (typeof window !== "undefined") {
      this.isDesktopSummaryLayout = window.innerWidth >= 770;
      this.isPdfOptionsExpanded = this.isDesktopSummaryLayout;
    }

    afterEveryRender(() => {
      this.detectTextOverflow();
    });
  }

  private loadingTimer: ReturnType<typeof setTimeout> | null = null;

  private applyState(state: UserSummaryState): void {
    const shouldLoad = state.status === 'loading' || state.status === 'idle';
    if (shouldLoad) {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
      this.isLoading.set(true);
    } else if (this.isLoading()) {
      // Minimum skeleton display: 300ms to avoid flash
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
      }
      this.loadingTimer = setTimeout(() => {
        this.isLoading.set(false);
        this.loadingTimer = null;
      }, 300);
    }
    this.breadcrumbs = state.breadcrumbs ?? [];
    this.selectedUserWithUserData = state.selectedUserWithUserData;
    this.selectedSectionGroupUserDataAttributes =
      state.selectedSectionGroupUserDataAttributes ?? [];
    this.buildLabelSizeClassMap(this.selectedSectionGroupUserDataAttributes);
    this.SelectedSectionGroupFunctions =
      state.selectedSectionGroupFunctions ?? [];
    this.userDataList = state.userDataList ?? [];
    this.userDataId0 = state.userDataId0;
    this.userDataId1 = state.userDataId1;
    this.userDataDate0 = state.userDataDate0;
    this.userDataDate1 = state.userDataDate1;
    this.uploadAsOfDate = state.uploadAsOfDate;
    this.isAnonymous = state.isAnonymous;

    this.filterPdfOptions();
    this.KTChartValues = state.chartValues ?? this.KTChartValues;
    this.dynamicData = buildKtChartMergeOption(this.KTChartValues);
    void this.schedulePdfChartUpdate([...this.KTChartValues]);
    this.cdr.markForCheck();
  }

  private filterPdfOptions(): void {
    if (this.SelectedSectionGroupFunctions?.length) {
      this.filteredPdfOptions = this.pdfOptions.filter((option) =>
        option.attribute
          ? this.SelectedSectionGroupFunctions.includes(option.attribute)
          : true,
      );
    } else {
      this.filteredPdfOptions = [];
    }

    this.loadPdfOptions();
  }

  /**
   * キャッシュからラベルサイズクラスを返す（テンプレート用）。
   * 毎回の変更検知で再計算しないので、オートコンプリート等に干渉しない。
   */
  getLabelSizeClass(group: { name?: string }): string {
    return this.labelSizeClassMap[group?.name ?? ''] ?? 'dl-table--label-m';
  }

  /**
   * 全グループのラベルサイズクラスを事前計算してキャッシュする。
   * applyState() から呼ばれる。
   */
  private buildLabelSizeClassMap(groups: any[]): void {
    const map: Record<string, string> = {};
    for (const group of groups) {
      if (!group?.dataItems?.length) {
        map[group?.name ?? ''] = 'dl-table--label-m';
        continue;
      }
      const maxLen = Math.max(
        ...group.dataItems
          .filter((item: any) => item.enabled)
          .map((item: any) => {
            const name = this.common.getDispName(item) ?? '';
            return Array.from(name as string).length;
          }),
      );
      if (maxLen <= 6) {
        map[group.name] = 'dl-table--label-s';
      } else if (maxLen <= 10) {
        map[group.name] = 'dl-table--label-m';
      } else {
        map[group.name] = 'dl-table--label-l';
      }
    }
    this.labelSizeClassMap = map;
  }

  getKtChartColumns<T extends { name?: string }>(
    dataItems: T[] | null | undefined,
  ): Array<Array<{ item: T; index: number }>> {
    if (!Array.isArray(dataItems) || dataItems.length === 0) {
      return [[], []];
    }
    const entries = dataItems.map((item, index) => ({ item, index }));
    const half = Math.ceil(entries.length / 2);
    return [entries.slice(0, half), entries.slice(half)];
  }

  onResize() {
    this.adjustChartSize();
    this.updatePdfOptionsExpansionForViewport();
  }

  togglePdfOptions(): void {
    this.isPdfOptionsExpanded = !this.isPdfOptionsExpanded;
  }

  getPdfOptionsSummaryLabel(): string {
    if (!this.filteredPdfOptions || this.filteredPdfOptions.length === 0) {
      return this.i18n.pdfOptions.notAvailable;
    }
    const selectedCount = this.filteredPdfOptions.filter(
      (option) => option.checked,
    ).length;
    if (selectedCount === 0) {
      return this.i18n.pdfOptions.noneSelected;
    }
    return this.i18n.pdfOptions.selectedCount(selectedCount);
  }

  private updatePdfOptionsExpansionForViewport(): void {
    if (typeof window === "undefined") {
      return;
    }
    const isDesktop = window.innerWidth >= 770;
    if (isDesktop !== this.isDesktopSummaryLayout) {
      this.isDesktopSummaryLayout = isDesktop;
      this.isPdfOptionsExpanded = isDesktop;
    }
  }
  onAutoInputChange(value: string, dataItem: any) {
    void this.formService.ensureStaffOptionsLoaded().then(() => {
      this.filteredStaffOptions[dataItem.name] =
        this.formService.filterStaffOptions(value, dataItem.name);
      this.cdr.markForCheck();
    });
  }

  get summaryDatesTooltip(): string {
    const display = this.userDataDate0 ?? "-";
    const compare = this.userDataDate1 ?? "-";
    return `${this.i18n.tooltip.displayData(display)}\n${this.i18n.tooltip.compareData(compare)}`;
  }

  ngOnInit() {
    for (
      let i = 0;
      i < this.selectedSectionGroupUserDataAttributes.length;
      i++
    ) {
      const userDataGroup = this.selectedSectionGroupUserDataAttributes[i];
      if (userDataGroup.dataItems) {
        for (let j = 0; j < userDataGroup.dataItems.length; j++) {
          const dataItem = userDataGroup.dataItems[j];
          if (dataItem.type === "PersonName") {
            this.filteredStaffOptions[dataItem.name] = [];
          }
        }
      }
    }

    void this.formService.ensureStaffOptionsLoaded();

    this.vm$
      .pipe(
        delay(0), // Async boundary: ensures skeleton renders before sync cache data arrives
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.applyState(state));

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      this.userId = params["userId"];
      if (!this.userId) {
        this.router.navigate(["/system-error"]);
        return;
      }

      this.viewModel.init(this.userId);

      this.isAnonymous = this.userId === "ANONYMOUS";
      this.cdr.markForCheck();
    });
  }
  ngAfterViewInit() {
    this.adjustChartSize();
  }

  uploadDateClass: MatCalendarCellClassFunction<moment.Moment> = (
    cellDate,
    view,
  ) => {
    const date = cellDate.format("YYYY-MM-DD");

    for (var i = 0; i < this.userDataList.length; i++) {
      if (this.userDataList[i].asOfDate == date) {
        return "updated-date-class";
      }
    }

    if (this.mode == Mode.input) {
      return "not-updated-date-for-input-class";
    } else {
      return "not-updated-date-class";
    }
  };

  onUploadDateChange(): void {
    const element = document.getElementById(
      "upload_date0",
    ) as HTMLInputElement | null;
    if (!element) {
      return;
    }
    const selectedValue = element.value;
    if (this.mode === Mode.input) {
      this.uploadAsOfDate = selectedValue.replace(/\//g, "-");
      return;
    }

    if (!selectedValue) {
      return;
    }

    const normalized = selectedValue.replace(/\//g, "-");
    this.viewModel.selectAsOfDate(normalized);
  }

  setToToday() {
    (document.getElementById("upload_date0") as HTMLInputElement).value =
      this.getFormattedDate(Date.now(), "yyyy/mm/dd");
    this.uploadAsOfDate = this.getFormattedDate(Date.now(), "yyyy-mm-dd");
  }

  setToPrevDay() {
    var prevDate = new Date(this.uploadAsOfDate);
    prevDate.setDate(prevDate.getDate() - 1);
    this.uploadAsOfDate = this.getFormattedDate(prevDate, "yyyy-mm-dd");
  }

  setToNextDay() {
    var nextDate = new Date(this.uploadAsOfDate);
    nextDate.setDate(nextDate.getDate() + 1);
    this.uploadAsOfDate = this.getFormattedDate(nextDate, "yyyy-mm-dd");
  }

  getIdFromDate(asOfDate) {
    for (var i = 0; i < this.userDataList.length; i++) {
      if (this.userDataList[i].asOfDate == asOfDate) {
        return this.userDataList[i].id;
      }
    }
    return null;
  }

  uploadDateToId(dateString) {
    const date = moment(dateString, "YYYY/MM/DD").toDate();
    const formattedDate = moment(date).format("YYYY-MM-DD");
    for (var i = 0; i < this.userDataList.length; i++) {
      if (this.userDataList[i].asOfDate == formattedDate) {
        return this.userDataList[i].id;
      }
    }

    return null;
  }

  getUserDataItemValueCompare(key, dispGeneration, compareGeneration) {
    if (this.selectedUserWithUserData === null) {
      return "-";
    }
    var newestVal = this.getUserDataItemValue(key, dispGeneration);
    var secondNewestVal = this.getUserDataItemValue(key, compareGeneration);

    if (newestVal === "" || newestVal === null || newestVal === undefined) {
      return "-";
    }

    var retVal = newestVal;

    if (secondNewestVal === "" || secondNewestVal === null) {
      secondNewestVal = "-";
    }

    var updownarrow = 0;
    if (newestVal > secondNewestVal) updownarrow = 2;
    else if (newestVal == secondNewestVal) updownarrow = 1;
    else updownarrow = 0;

    newestVal = newestVal.toString();
    secondNewestVal = secondNewestVal.toString();

    if (secondNewestVal !== "") {
      if (newestVal.indexOf("\n") > 0 || secondNewestVal.indexOf("\n") > 0) {
        retVal = retVal + "\n";
      }
      retVal =
        retVal + ' <span class="text_red"> (' + secondNewestVal + ")</span>";
    }

    return retVal;
  }
  getUserDataItemValueComparewithArrow(key, dispGeneration, compareGeneration) {
    if (this.selectedUserWithUserData === null) {
      return "-";
    }
    var newestVal = this.getUserDataItemValue(key, dispGeneration);
    var secondNewestVal = this.getUserDataItemValue(key, compareGeneration);

    if (newestVal === "" || newestVal === null || newestVal === undefined) {
      return "-";
    }

    if (secondNewestVal === "" || secondNewestVal === null) {
      secondNewestVal = "-";
      return "-";
    }

    var retVal = "";

    var newestValString = newestVal.toString();
    var secondNewestValString = secondNewestVal.toString();
    var CompareVal = 0;
    CompareVal = Number(newestVal) - Number(secondNewestVal);

    if (secondNewestValString !== "") {
      if (
        newestValString.indexOf("\n") > 0 ||
        secondNewestValString.indexOf("\n") > 0
      ) {
        retVal = retVal + "\n";
      }
      if (newestVal > secondNewestVal) {
        retVal = "↑";
      } else if (newestVal == secondNewestVal) {
        retVal = "←";
        return retVal;
      } else {
        retVal = "↓";
        CompareVal = CompareVal * -1;
      }

      var CompareValString = CompareVal.toString();
      retVal = retVal + CompareValString;
    }

    return retVal;
  }
  getArrowColor(key, dispGeneration, compareGeneration) {
    if (this.selectedUserWithUserData === null) {
      return "";
    }
    var newestVal = this.getUserDataItemValue(key, dispGeneration);
    var secondNewestVal = this.getUserDataItemValue(key, compareGeneration);

    if (newestVal === "" || newestVal === null || newestVal === undefined) {
      return "";
    }

    if (secondNewestVal === "" || secondNewestVal === null) {
      secondNewestVal = "-";
      return "";
    }

    var retVal = "";

    var newestValString = newestVal.toString();
    var secondNewestValString = secondNewestVal.toString();
    var CompareVal = 0;
    CompareVal = Number(newestVal) - Number(secondNewestVal);

    if (secondNewestValString !== "") {
      if (
        newestValString.indexOf("\n") > 0 ||
        secondNewestValString.indexOf("\n") > 0
      ) {
        retVal = retVal + "\n";
      }
      if (Number(newestVal) > Number(secondNewestVal)) {
        retVal = "text_red";
      } else if (Number(newestVal) == Number(secondNewestVal)) {
        retVal = "text_gray";
      } else {
        retVal = "text_blue";
      }
    }
    return retVal;
  }

  getUserDataOfGeneration(generation) {
    if (generation == null || generation == undefined) {
      return null;
    }

    if (
      this.selectedUserWithUserData === null ||
      this.selectedUserWithUserData === undefined
    ) {
      return null;
    }

    const userData = this.selectedUserWithUserData.userData[generation];

    if (userData !== null && userData !== undefined) {
      return userData;
    }

    if (
      this.userDataList === null ||
      this.userDataList === undefined ||
      this.userDataList.length === 0
    ) {
      return null;
    }

    const userDatas = this.userDataList.filter((u) => u.id == generation);
    if (userDatas !== null) {
      if (userDatas.length > 0) {
        return userDatas[0];
      } else {
        return null;
      }
    }

    return null;
  }

  getDateItemDefinition(dataItemName) {
    try {
      for (
        var i = 0;
        i < this.selectedSectionGroupUserDataAttributes.length;
        i++
      ) {
        var userDataGroup = this.selectedSectionGroupUserDataAttributes[i];
        for (var j = 0; j < userDataGroup.dataItems.length; j++) {
          var dataItem = userDataGroup.dataItems[j];
          if (dataItem.name == dataItemName) {
            return dataItem;
          }
        }
      }
    } catch {
      return "";
    }
  }

  /** 長文テキストフィールドの展開/折りたたみをトグル */
  toggleFieldExpand(fieldName: string): void {
    this.expandedFields.update(fields => ({
      ...fields,
      [fieldName]: !fields[fieldName],
    }));
  }

  /** クランプされた要素の実際のオーバーフローを検出 */
  private detectTextOverflow(): void {
    const host = this.hostRef.nativeElement as HTMLElement;
    const clampedEls = host.querySelectorAll<HTMLElement>('.value-text--clamped');
    const newOverflowing = new Set<string>();

    // 展開中のフィールドは引き続きオーバーフロー扱い（閉じるボタンを維持）
    const expanded = this.expandedFields();
    for (const name of Object.keys(expanded)) {
      if (expanded[name]) {
        newOverflowing.add(name);
      }
    }

    // クランプ中の要素で実際にオーバーフローしているものを検出
    clampedEls.forEach(el => {
      const fieldName = el.dataset['field'];
      if (fieldName && el.scrollHeight > el.clientHeight + 1) {
        newOverflowing.add(fieldName);
      }
    });

    const current = this.overflowingFields();
    const changed = newOverflowing.size !== current.size ||
      [...newOverflowing].some(f => !current.has(f));
    if (changed) {
      this.overflowingFields.set(newOverflowing);
    }
  }

  getUserDataItemValue(dataItemName, generation) {
    const userData = this.getUserDataOfGeneration(generation);
    if (userData === null) {
      return "";
    }

    var value = "";
    if (dataItemName in userData.data) {
      value = userData.data[dataItemName];

      try {
        const dataItem = this.getDateItemDefinition(dataItemName);
        const dataItemType = dataItem.type;
        if (dataItemType.toLowerCase() == "bool") {
          if (value.toLowerCase() == "true") {
            value = this.i18n.boolValue.present;
          }
          if (value.toLowerCase() == "false") {
            value = this.i18n.boolValue.absent;
          }
        }

        if (dataItemType.toLowerCase() == "list") {
          value = this.common.getListItemDispValue(dataItem.values, value);
        }
      } catch {}
    }

    return value;
  }

  changeToInputMode() {
    this.mode = Mode.input;
    this.inputValidationErrors = [];

    if (this.userDataDate0 != null) {
      this.uploadAsOfDate = this.userDataDate0;
    } else {
      this.uploadAsOfDate = this.getFormattedDate(Date.now(), "yyyy-mm-dd");
    }
  }
  changeToDisplayMode() {
    this.mode = Mode.display;
    this.inputValidationErrors = [];
  }

  getGenderColor(gender) {
    var gender_class;
    if (gender == "male") {
      gender_class = "color-male";
    } else if (gender == "female") {
      gender_class = "color-female";
    }
    return gender_class;
  }

  getCreatedAt(generation) {
    if (!["newest", "secondNewest"].includes(generation)) {
      return "-";
    }

    try {
      var createDateTimeUTC =
        this.selectedUserWithUserData.userData[generation].data.createdAt;

      let date = new Date(createDateTimeUTC);
      const retDate = date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return retDate;
    } catch {
      return "-";
    }
  }

  uploadUserData() {
    this.inputValidationErrors = [];

    var userData = {};
    for (const userDataGroup of this.selectedSectionGroupUserDataAttributes) {
      for (const dataItem of userDataGroup.dataItems) {
        if (!("name" in dataItem)) {
          continue;
        }
        const dataItemName = dataItem.name;

        if (!("enabled" in dataItem)) {
          continue;
        }
        if (!dataItem.enabled) {
          continue;
        }

        var dataItemElement = document.getElementById(
          dataItemName,
        ) as HTMLInputElement;
        if (dataItemElement) {
          var dataValue = dataItemElement.value;
          userData[dataItemName] = dataValue;

          var dataItemDispLabel = this.common.getDispName(dataItem);
          if (dataValue === "") {
            if (dataItem.required) {
              this.inputValidationErrors.push(
                this.i18n.validationError.required(dataItemDispLabel),
              );
              this.inputValidationErrors = [...this.inputValidationErrors];
            }
          } else if (dataItem.type === "Int") {
            if (
              Number(dataValue) < Number(dataItem.min) ||
              Number(dataItem.max) < Number(dataValue)
            ) {
              this.inputValidationErrors.push(
                this.i18n.validationError.outOfRange(dataItemDispLabel, dataItem.min, dataItem.max),
              );
              this.inputValidationErrors = [...this.inputValidationErrors];
            }
          }
        } else {
          this.inputValidationErrors.push(
            this.i18n.validationError.elementNotFound(dataItemDispLabel),
          );
          this.inputValidationErrors = [...this.inputValidationErrors];
        }
      }
    }

    if (this.inputValidationErrors.length > 0) {
      window.scroll({ top: 0, left: 0, behavior: "smooth" });
      return;
    }

    const uploadDateElement = document.getElementById(
      "upload_date0",
    ) as HTMLInputElement | null;
    const rawAsOfDate = uploadDateElement?.value ?? "";
    const asOfDate = rawAsOfDate.replace(/\//g, "-");

    let postParams = {
      givenTo: this.common.getSelectedUserId(),
      userData: userData,
      createdAt: this.getFormattedDate(new Date(), "yyyy-mm-ddThh:nn:ss.sssoo"),
      asOfDate: this.getFormattedDate(asOfDate, "yyyy-mm-dd"),
    };

    var existingData = this.userDataList.find(
      (u) => u.asOfDate == postParams.asOfDate,
    );
    if (!existingData) {
      this.createUserData(postParams);
    } else {
      var mediaDeleteDialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: false,
        data: {
          title: this.i18n.overwriteDialog.title,
          message: this.i18n.overwriteDialog.message,
        },
      });

      mediaDeleteDialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {

        if (result) {
          this.createUserData(postParams);
        }
      });
    }
  }

  createUserData(postParams) {
    this.isSaving.set(true);
    this.createUserDataService.createUserData(postParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
      (res) => {
        this.isSaving.set(false);
        this.common.prepareUserDataList(this.userId, true);

        this.snackBar.open(this.i18n.snackBar.uploadSuccess, this.i18n.snackBar.close, {
          duration: 3000,
        });

        this.mode = Mode.display;
        this.viewModel.selectAsOfDate(postParams.asOfDate);

        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        this.cdr.markForCheck();
      },
      (error) => {
        this.isSaving.set(false);
        this.inputValidationErrors.push(
          this.i18n.validationError.uploadFailed,
        );
        this.inputValidationErrors = [...this.inputValidationErrors];
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
      },
    );
  }

  getFormattedDate(dateString, format) {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");

      const hour = date.getHours().toString().padStart(2, "0");
      const min = date.getMinutes().toString().padStart(2, "0");
      const second = date.getSeconds().toString().padStart(2, "0");

      const millSec = date.getMilliseconds().toString().padStart(3, "0");

      const timezoneOffset = date.getTimezoneOffset() / 60;
      const offsetString =
        timezoneOffset < 0
          ? `+${Math.abs(timezoneOffset).toString().padStart(2, "0")}:00`
          : `-${timezoneOffset.toString().padStart(2, "0")}:00`;

      const formattedDate = format
        .replace("yyyy", year)
        .replace("mm", month)
        .replace("dd", day)
        .replace("hh", hour)
        .replace("nn", min)
        .replace("sss", millSec)
        .replace("ss", second)
        .replace("oo", offsetString);

      return formattedDate;
    } catch {
      return "-";
    }
  }

  logToConsole(msg) {
  }

  async downloadPdf(): Promise<void> {
    try {
      await this.schedulePdfChartUpdate([...this.KTChartValues]);
      const chartImageDataUrl = this.pdfChartImageDataUrl ?? null;
      const sectionDocuments = this.getSectionDocumentsFromStorage();
      if (!sectionDocuments) {
        this.snackBar.open(
          this.i18n.pdfError.sectionDocumentsMissing,
          undefined,
          { duration: 4000 },
        );
        return;
      }

      const data = this.getUserDataOfGeneration(this.userDataId0);
      if (!data) {
        this.snackBar.open(
          this.i18n.pdfError.userDataMissing,
          undefined,
          { duration: 4000 },
        );
        return;
      }

      await this.userSummaryPdfService.generate({
        sectionDocuments,
        sectionId: this.common.getSelectedSection()?.sectionId ?? null,
        userId: this.userId,
        userDataDate: this.userDataDate0,
        userDataAttributes: this.selectedSectionGroupUserDataAttributes ?? [],
        pdfOptions: this.pdfOptions,
        userData: data,
        selectedUserWithUserData: this.selectedUserWithUserData,
        chartImageDataUrl,
      });
    } catch (err) {
      console.error("[ERROR] user-summary.component.ts > downloadPdf()", err);
      this.snackBar.open(this.resolvePdfErrorMessage(err), undefined, {
        duration: 4000,
      });
    }
  }

  onChartInit(): void {
    this.isChartInitialized = true;
    this.adjustChartSize();
  }

  adjustChartSize(): void {
    if (!this.isChartInitialized) {
      return;
    }
    const host = this.chartElementRef?.nativeElement;
    if (!host) {
      return;
    }
    const sizing = calculateKtChartSizing(window.innerWidth, host.offsetWidth);
    this.KTchartOption = createKtChartBaseOption(sizing);
    this.dynamicData = buildKtChartMergeOption(this.KTChartValues);
  }

  private async schedulePdfChartUpdate(values: number[]): Promise<void> {
    if (this.pdfChartGenerationPromise) {
      await this.pdfChartGenerationPromise;
      return;
    }

    this.pdfChartGenerationPromise = this.createChartImageForPdf(values)
      .then((data) => {
        this.pdfChartImageDataUrl = data;
        return data;
      })
      .catch((error) => {
        console.error(
          "[ERROR] user-summary.component.ts > schedulePdfChartUpdate()",
          error,
        );
        this.pdfChartImageDataUrl = null;
        return null;
      })
      .finally(() => {
        this.pdfChartGenerationPromise = null;
      });

    await this.pdfChartGenerationPromise;
  }

  private async createChartImageForPdf(
    values: number[],
  ): Promise<string | null> {
    if (typeof document === "undefined") {
      return null;
    }

    const width = 640;
    const height = 400;
    const pdfFontSize = 20;
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.pointerEvents = "none";
    document.body.appendChild(container);

    let chart: echarts.ECharts | null = null;
    try {
      chart = echarts.init(container, undefined, {
        renderer: "canvas",
        width,
        height,
      });
      const normalizedValues = values.map((value) =>
        Number.isFinite(value) ? value : Number(value) || 0,
      );
      const baseOption = createKtChartBaseOption({ fontSize: pdfFontSize });
      const mergeOption = buildKtChartMergeOption(normalizedValues);
      const finalSeries = Array.isArray(baseOption.series)
        ? baseOption.series.map((series) => {
            if ((series as any).type !== "radar") {
              return series;
            }
            const firstPoint =
              Array.isArray(series.data) && series.data.length > 0
                ? (series.data[0] as echarts.RadarSeriesOption["data"][number])
                : ({
                    value: new Array(normalizedValues.length).fill(0),
                  } as echarts.RadarSeriesOption["data"][number]);
            return {
              ...series,
              data: [
                {
                  ...firstPoint,
                  value: normalizedValues,
                },
              ],
            };
          })
        : baseOption.series
          ? [
              {
                ...baseOption.series,
                data: [
                  {
                    ...(Array.isArray(baseOption.series.data) &&
                    baseOption.series.data.length > 0
                      ? baseOption.series.data[0]
                      : { value: new Array(normalizedValues.length).fill(0) }),
                    value: normalizedValues,
                  },
                ],
              },
            ]
          : [];
      chart.setOption(
        {
          ...baseOption,
          series: finalSeries as typeof baseOption.series,
          animation: false, // Disable animation for PDF export for performance within 100ms timeout
        },
        { notMerge: true },
      );

      await new Promise<void>((resolve) => {
        if (!chart) {
          resolve();
          return;
        }
        const handler = () => {
          chart?.off("finished", handler);
          resolve();
        };
        chart.on("finished", handler);
        setTimeout(handler, 100); // Safety timeout
      });

      return chart.getDataURL({
        pixelRatio: 2,
        backgroundColor: "rgba(0,0,0,0)",
      });
    } catch (error) {
      console.error(
        "[ERROR] user-summary.component.ts > createChartImageForPdf()",
        error,
      );
      return null;
    } finally {
      if (chart) {
        chart.dispose();
      }
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }
  }

  onPdfOptionsCheckboxChange() {
    this.savePdfOptions();
  }

  getPdfOptionsStorageKey() {
    const selectedSection = this.common.getSelectedSection();
    return `gokuri.pdfOptions_${selectedSection.sectionId}_${this.userId}`;
  }

  savePdfOptions() {
    const dataToSave = this.pdfOptions.map((option) => ({
      label: option.label,
      checked: option.checked,
    }));
    localStorage.setItem(
      this.getPdfOptionsStorageKey(),
      JSON.stringify(dataToSave),
    );
  }

  loadPdfOptions() {
    const savedData = localStorage.getItem(this.getPdfOptionsStorageKey());

    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        for (let i = 0; i < this.pdfOptions.length; i++) {
          const baseOption = this.pdfOptions[i];
          const savedOption = parsedData.find(
            (option) => option.label === baseOption.label,
          );
          if (savedOption) baseOption.checked = savedOption.checked;
        }
      } catch (error) {
      }
    }
  }

  private getSectionDocumentsFromStorage(): any[] | null {
    if (typeof window === "undefined") {
      return null;
    }
    const sectionDocumentsStr = sessionStorage.getItem("sectionDocuments");
    if (!sectionDocumentsStr) {
      return null;
    }
    try {
      return JSON.parse(sectionDocumentsStr);
    } catch (error) {
      console.error(
        "[ERROR] user-summary.component.ts > getSectionDocumentsFromStorage()",
        error,
      );
      return null;
    }
  }

  private resolvePdfErrorMessage(error: unknown): string {
    const code = error instanceof Error ? error.message : "";
    switch (code) {
      case "SECTION_DOCUMENTS_MISSING":
        return this.i18n.pdfError.sectionDocumentsMissing;
      case "SECTION_DOCUMENT_NOT_FOUND":
        return this.i18n.pdfError.sectionDocumentNotFound;
      case "SECTION_ID_MISSING":
        return this.i18n.pdfError.sectionIdMissing;
      case "USER_DATA_MISSING":
        return this.i18n.pdfError.userDataMissing;
      default:
        return this.i18n.pdfError.generateFailed;
    }
  }
}

export enum Mode {
  display,
  input,
  overwrite,
}
